import { SpecificOrAnyString } from "@jugl-web/utils";
import { PaginatedResponse } from "../../types";
import { CreateSubtaskDto, SubtaskDto } from "./models/Subtask/SubtaskDto";
import { TaskDetailsBoardDto } from "./models/TaskBoard/TaskBoardDto";
import { TaskCustomFieldDto } from "./models/TaskCustomField";
import {
  ExpectedTaskCustomDropdownFieldValue,
  TaskCustomFieldModel,
} from "./models/TaskCustomField/TaskCustomFieldModel";
import { TaskCustomStatusDto } from "./models/TaskCustomStatus/TaskCustomStatusDto";
import { TaskCustomStatusModel } from "./models/TaskCustomStatus/TaskCustomStatusModel";

// TODO: Divide types into DTOs, models, and payloads
export interface TaskAssignee {
  first_name: string;
  img: string;
  last_name: string;
  nickname: string;
  status: string;
  user_id: string;
}

export interface TaskChecklistItem {
  completed_at: string | null;
  due_at: string | null;
  id: string;
  is_completed: boolean;
  name: string;
  order: number;
  user_id: string | null;
  attachments?: TaskAttachment[] | null;
  attachment_count?: number;
}

export type TaskChecklistSummaryItem = {
  id: string;
  due_at: string | null;
  name: string;
  completed_at: string;
  task_id: string;
  task_name: string;
};

export type ChecklistSummary = {
  checklists: TaskChecklistSummaryItem[];
  user_id: string;
};

export type TaskLabel = {
  id: string;
  text: string;
  color: string;
  order: number;
};

export enum TaskPriority {
  none = "none",
  low = "low",
  medium = "medium",
  high = "high",
}

export enum TaskDefaultStatusId {
  pending = "0",
  completed = "1",
}

export enum TaskType {
  voice = "voice_task",
  overdue = "overdue",
  recurring = "recurring",
  template = "template",
}

export enum TaskSortBy {
  DueAt = "due_at",
}

export type TaskAttachment = {
  content_type: string;
  id: string;
  name: string;
  preview_available: boolean;
  preview_url: string;
  size: number;
  status: "valid" | "invalid";
  stream_url: string;
  updated_at: string;
};

export type TaskCustomer = {
  id: string;
  logo: string | null;
  fields: { id: string; name: string; value: string }[];
};

export type TaskCreator = {
  first_name: string;
  img: string | null;
  last_name: string;
  nickname: string;
  // TODO: import status from user
  status: unknown;
  user_id: string;
};

export interface TaskRecurrence {
  end_at: TaskRecurrenceEndAt;
  interval: TaskRecurrenceInterval;
  tz: string;
  enabled?: boolean;
}

export enum TaskRecurrenceIntervalEvery {
  day = "day",
  week = "week",
  month = "month",
  year = "year",
}

export type TaskRecurrenceInterval =
  | {
      every: TaskRecurrenceIntervalEvery.day;
      freq: number;
    }
  | {
      every: TaskRecurrenceIntervalEvery.week;
      freq: number;
      on: number[];
    }
  | {
      every: TaskRecurrenceIntervalEvery.month;
      freq: number;
      on: { date: number } | { week: number; day: number };
    }
  | {
      every: TaskRecurrenceIntervalEvery.year;
      freq: number;
      on: { month: number; date: number };
    };

export type TaskRecurrenceEndAt = null | { date: string } | { counter: number };

export interface TaskReminder {
  next_execution: string;
}

export interface BaseTask {
  assignees: string[];
  board_id: string | null;
  chklist_overdue: boolean;
  chklist_completed: number;
  chklist_total: number;
  created_at: string;
  created_by: string;
  cust_id: string | null;
  customer: {
    email: string | null;
    first_name: string;
    id: string;
    last_name: string | null;
    logo: string | null;
    phone_number: string | null;
  } | null;
  custom_fields: Record<string, string> | null;
  due_at: string | null;
  time_tracked: number;
  duration: number;
  entity_id: string;
  has_chklist_chk: boolean;
  id: string;
  is_self: boolean;
  is_manual_order: boolean;
  label_id: string | null;
  name: string;
  order_id: string | null;
  order_resp: string | null;
  priority: TaskPriority;
  recurrence: TaskRecurrence | null;
  recurrences: string[];
  reminder: TaskReminder | null;
  short_url: string;
  status: string;
  tz: string | null;
  unread: boolean;
  updated_at: string;
}

export interface PreviewTask extends BaseTask {
  /**
   * Internal field to determine if the task is a future occurrence of a recurring task.
   */
  _future_task?: {
    source_task_id: string;
    recurrence_date: Date;
  };
}

export interface DetailedTask extends BaseTask {
  board: TaskDetailsBoardDto;
  completed_on: string | null;
  desc: string | null;
  type: string | null;
  order_count: number;
  order_resp_count: number;
  attachment_count: number;
  history_count: number;
  invoice_count: number;
  cmnt_unread: boolean;
  feedback_unread: boolean;
  version: number;
  time_logs_cnt: number;
  _is_deleted?: boolean;
}

export type AnyTask = PreviewTask | DetailedTask;

interface ArchivedBaseTask {
  assignees: string[];
  board_id: string | null;
  cust_id: string | null;
  due_at: string | null;
  id: string;
  label_id: string | null;
  name: string;
  status: string;
  order_id: string | null;
  order_resp: string | null;
  is_manual_order: boolean;
}

export interface ArchivedPreviewTask extends ArchivedBaseTask {
  inserted_at: string;
}

export interface ArchivedDetailedTask extends ArchivedBaseTask {
  attachment_count: number;
  board: TaskDetailsBoardDto | null;
  can_assignee_edit: boolean;
  checklist: SubtaskDto[];
  cmnt_unread: boolean;
  completed_on: string | null;
  created_at: string;
  created_by: string;
  customer: TaskCustomer | null;
  custom_fields: Record<string, string> | null;
  desc: string | null;
  duration: number;
  entity_id: string;
  feedback_unread: boolean;
  has_chklist_chk: boolean;
  is_recurring: boolean;
  is_self: boolean;
  priority: TaskPriority;
  recurrence: TaskRecurrence | null;
  recurrences: string[];
  reminder: TaskReminder | null;
  restored_at: string | null;
  short_url: string | null;
  tz: string | null;
  type: string | null;
  time_tracked: number;
  unread: boolean;
  updated_at: string;
  version: number;
}

export interface TaskFilters {
  board: (string | null)[] | null;
  inv_item: (string | null)[] | null;
  assignee: (string | null)[] | null;
  custom_fields: Record<string, (string | null)[]> | null;
  customer: (string | null)[] | null;
  label: (string | null)[] | null;
  overdue: true | null;
  priority: TaskPriority[] | null;
  recurring: true | null;
  status: string[] | null;
}

export interface TaskFilterSet extends TaskFilters {
  id: string;
  name: string;
  updated_at: string;
}

export interface TasksInitData {
  custom_fields: {
    entity_id: string;
    id: string;
    props: {
      value: TaskCustomFieldDto[];
    };
    type: "task_fields";
  };
  custom_statuses: TaskCustomStatusDto[];
  filter_sets: TaskFilterSet[];
  labels: TaskLabel[];
}

export interface InternalTaskFilters {
  boards: (string | null)[];
  inventoryItems: (string | null)[];
  labels: (string | null)[];
  priorities: TaskPriority[];
  statuses: string[];
  assignees: (string | null)[];
  customers: (string | null)[];
  customFields: Record<string, (string | null)[]>;
  isOverdue: true | null;
  isRecurring: true | null;
}

export interface InternalTaskFilterSet {
  id: string;
  name: string;
  filters: InternalTaskFilters;
  updatedAt: string;
}

export interface TaskInitData {
  customFieldsData: {
    id: string;
    items: TaskCustomFieldModel[];
  };
  labels: TaskLabel[];
  customStatuses: TaskCustomStatusModel[];
  filterSets: InternalTaskFilterSet[];
}

export type UpdatedActionChanges = Partial<
  Pick<
    DetailedTask,
    | "name"
    | "desc"
    | "board"
    | "priority"
    | "due_at"
    | "status"
    | "duration"
    | "has_chklist_chk"
  >
> & {
  assignee_added?: {
    first_name: string;
    last_name: string;
    user_id: string;
  }[];
  assignee_removed?: {
    first_name: string;
    last_name: string;
    user_id: string;
  }[];
  checklist_updated?: {
    id: string;
    c_name: string;
    name?: string;
    is_completed?: boolean;
    completed_at?: string;
  }[];
  checklist_rearranged?: [];
  checklist_deleted?: {
    id: string;
    c_name: string;
  }[];
  checklist_added?: {
    id: string;
    c_name: string;
  }[];
  checklist_moved?: {
    id: string;
    c_name: string;
  }[];
  from_task?: {
    id: string;
    name: string;
  };
  to_task?: {
    id: string;
    name: string;
  };
  label?: Omit<TaskLabel, "order">;
  custom_fields?: NonNullable<DetailedTask["custom_fields"]>;
  old_custom_fields?: NonNullable<DetailedTask["custom_fields"]>;
  attachment_added?: {
    file_name: string;
  };
  attachment_deleted?: {
    file_name: string;
  };
  attachment_renamed?: {
    file_name: string;
    old_file_name: string;
  };
  customer?: "updated";
  reminder?: "updated";
  recurrence?: "updated";
};

export type UpdatedActionOldValues = Partial<
  Pick<
    DetailedTask,
    "name" | "board" | "desc" | "priority" | "due_at" | "status" | "duration"
  >
> & {
  label_id?: string | null;
  custom_fields?: NonNullable<DetailedTask["custom_fields"]>;
};

export type TaskActivityActionDetails =
  | {
      action: "created" | "deleted";
      changes: { title: string; time: string };
      task: TaskActivityTaskInfo;
    }
  | {
      action: "updated";
      changes: UpdatedActionChanges;
      task: TaskActivityTaskInfo;
      old?: UpdatedActionOldValues;
    }
  | {
      action: "viewed";
    }
  | {
      action:
        | "commented"
        | "guest_comment"
        | "guest_comment_reply"
        | "guest_opened";
      changes: { content: string };
      attachments?: string[];
      task: TaskActivityTaskInfo;
      user_name?: string;
    }
  | {
      action: "downloaded" | "guest_downloaded";
      file_name: string;
    }
  | {
      action: "guest_feedback";
      user_name?: string;
      changes: {
        content: {
          feedback: string;
          hashtag: string[];
          rating: number;
        };
      };
      task: TaskActivityTaskInfo;
    }
  | {
      action: "restored" | "archived";
      changes: {
        time: string;
      };
      task: TaskActivityTaskInfo;
    };

type TaskActivityTaskInfo = {
  id: string;
  title: string;
};

export interface TaskActivity {
  action_by: {
    user_id: string;
    first_name: string;
    last_name: string;
    img: string | null;
    updated_at: string;
    logo?: string;
  };
  action_details: TaskActivityActionDetails;
  id: string;
  inserted_at: string;
  task_id: string;
  entity_id: string;
  updated_at: string;
}

export type CreateTaskPayload = Pick<
  DetailedTask,
  | "assignees"
  | "board_id"
  | "cust_id"
  | "custom_fields"
  | "desc"
  | "due_at"
  | "has_chklist_chk"
  | "label_id"
  | "name"
  | "priority"
  | "recurrence"
  | "reminder"
  | "status"
  | "tz"
  | "order_id"
  | "order_resp"
> & {
  items: { inventory_id: string; qty: number }[] | undefined;
  checklist: (CreateSubtaskDto & { order: number })[];
  as_individual_tasks?: boolean;
  is_self?: boolean;
  attach_dir_id?: string;
};

export type CreateTaskCustomFieldPayload = Pick<
  TaskCustomFieldDto,
  "field" | "field_type" | "show_in_card" | "order"
> & { values: ExpectedTaskCustomDropdownFieldValue[] | null };

export type TasksBoardId = SpecificOrAnyString<"my" | "team">;

export type BoardTasksSource = { type: "boardTasks"; boardId: TasksBoardId };
export type CustomerTasksSource = { type: "customerTasks"; customerId: string };

export type TasksSource = BoardTasksSource | CustomerTasksSource;

export type ExportTaskVariants = "board" | "my" | "team" | "form" | "timelog";

type ExportTasksBaseParams = {
  from: string;
  to: string;
};

export type ExportTasksFormParams = ExportTasksBaseParams & {
  date_type: "inserted_at" | "completed_on" | "due_at";
  view: "form";
  form_id: string;
  prefix: string;
  board_id: string;
  status?: string;
};

export type ExportTasksDefaultParams = ExportTasksBaseParams & {
  date_type: "completed_on" | "due_at";
  view: "board" | "my" | "team";
  board?: string;
  user_id?: string;
  label?: string;
  no_due?: boolean;
  status?: string;
};

export type ExportTasksTimeLogsParams = ExportTasksBaseParams & {
  view: "timelog";
  board?: string;
  users?: string[];
};

export type ExportTasksParams =
  | ExportTasksFormParams
  | ExportTasksDefaultParams;

export type ExportTasksParamsWithoutView = Omit<
  ExportTasksDefaultParams,
  "view"
>;

export type ExportTasksFormParamsWithoutView = Omit<
  ExportTasksFormParams,
  "view"
>;

export type ExportTasksTimeLogsWithoutView = Omit<
  ExportTasksTimeLogsParams,
  "view"
>;

export type ExportBulkActionTasksParams = {
  tasks: string[];
  board?: string;
};

export type BoardTemplatesCategories = Record<string, string[]>;

export type BoardTemplate = {
  desc: string;
  id: string;
  inserted_at: string;
  name: string;
  img: string;
  updated_at: string;
};

export type BoardTemplatesList = PaginatedResponse<BoardTemplate>;

export type BoardTemplateDetails = {
  components: [
    | {
        type: "custom_field";
        data: string[];
        order: number;
      }
    | {
        type: "custom_status";
        data: string[];
        order: number;
      }
    | {
        type: "task";
        data: string[];
        order: number;
      },
  ];
  desc: string;
  full_desc: string;
  id: string;
  name: string;
  meta?: {
    board_id?: string;
  };
};

export interface TaskObjectNaming {
  singular: string;
  plural: string;
}

export const DEFAULT_TASK_OBJECT_NAMING: TaskObjectNaming = {
  singular: "Task",
  plural: "Tasks",
};
